import { useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useWeb3Auth } from '../../web3/Web3AuthContext';
import {
  listRegistryApplications,
  reviewOnRegistry,
  roleLabel,
  statusLabel,
} from '../../web3/registryClient';
import { fetchIpfsJson } from '../../web3/ipfs';
import {
  parseApplicationEnvelope,
  type ApplicationEnvelope,
} from '../../web3/applicationEnvelope';
import { openApplicantRecord } from '../../web3/applicantCrypto';
import { Check, X, Eye, Search } from 'lucide-react';
import { FixedSizeList as List } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import debounce from 'lodash.debounce';
import Section from '../../components/Section';
import Button from '../../components/Button';

type Application = {
  id: string;
  onChainId?: number;
  created_at: string;
  wallet_address?: string | null;
  did_id?: number | null;
  type: 'athlete' | 'agent' | 'officer' | 'representative';
  status: string;
  uri?: string;
  contentHash?: string;
};

const ITEMS_PER_PAGE = 50;

const AdminApplications = () => {
  const navigate = useNavigate();
  const web3 = useWeb3Auth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [applications, setApplications] = useState<Application[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('all');
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [detail, setDetail] = useState<{
    app: Application;
    envelope?: ApplicationEnvelope;
    plaintext?: Record<string, unknown>;
    error?: string;
  } | null>(null);
  const [reviewerSecret, setReviewerSecret] = useState('');
  const [detailLoading, setDetailLoading] = useState(false);
  const loadingRef = useRef(false);
  const allowed = web3.isConnected && web3.isAdmin;

  const toApplication = (app: {
    id: number;
    submittedAt: number;
    applicant: string;
    didId: number;
    appRole: number;
    status: number;
    uri: string;
    contentHash: string;
  }): Application => ({
    id: String(app.id),
    onChainId: app.id,
    created_at: new Date(app.submittedAt * 1000).toISOString(),
    wallet_address: app.applicant,
    did_id: app.didId,
    type: roleLabel(app.appRole),
    status: statusLabel(app.status),
    uri: app.uri,
    contentHash: app.contentHash,
  });

  // Memoize filtered applications
  const filteredApplications = useMemo(() => {
    return applications.filter(app => {
      const hay = `${app.wallet_address || ''} ${app.did_id ?? ''}`.toLowerCase();
      const matchesSearch = hay.includes(searchTerm.toLowerCase());
      const matchesFilter = filter === 'all' || app.status === filter;
      return matchesSearch && matchesFilter;
    });
  }, [applications, searchTerm, filter]);

  const loadApplications = async (_pageNumber: number) => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setIsLoading(true);
    setError(null);

    try {
      const chainId = web3.chainId || 2786;
      const rows = await listRegistryApplications(chainId);
      setApplications(
        rows
          .map(toApplication)
          .sort(
            (a, b) =>
              new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
          )
      );
      setHasMore(false);
    } catch (err) {
      console.error('Applications page error:', err);
      setError(err instanceof Error ? err.message : 'Failed to load applications');
    } finally {
      loadingRef.current = false;
      setIsLoading(false);
    }
  };

  // Debounced search handler
  const debouncedSearch = useMemo(
    () => debounce((term: string) => {
      setSearchTerm(term);
      setPage(0);
      setApplications([]);
      loadApplications(0);
    }, 300),
    []
  );

  useEffect(() => {
    if (!web3.isConnected) {
      navigate('/auth');
      return;
    }
    if (!web3.isAdmin) {
      navigate('/');
    }
  }, [navigate, web3.isAdmin, web3.isConnected]);

  useEffect(() => {
    if (!allowed) return;
    let cancelled = false;
    loadingRef.current = false;
    void (async () => {
      try {
        const rows = await listRegistryApplications(web3.chainId || 2786);
        if (cancelled) return;
        setApplications(
          rows
            .map(toApplication)
            .sort(
              (a, b) =>
                new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
            )
        );
        setHasMore(false);
        setError(null);
      } catch (err) {
        console.error('Applications page error:', err);
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load applications');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    setIsLoading(true);
    return () => {
      cancelled = true;
      debouncedSearch.cancel();
    };
  }, [allowed, debouncedSearch, web3.chainId]);

  const handleView = async (application: Application) => {
    setDetail({ app: application });
    if (!application.uri?.startsWith('ipfs://')) {
      setDetail({
        app: application,
        error: 'This application has no IPFS URI (legacy or failed pin).',
      });
      return;
    }
    setDetailLoading(true);
    try {
      const raw = await fetchIpfsJson(application.uri);
      const envelope = parseApplicationEnvelope(raw);
      let plaintext: Record<string, unknown> | undefined;
      if (reviewerSecret.trim()) {
        const opened = await openApplicantRecord(envelope.sealed, reviewerSecret.trim());
        plaintext = opened.plaintext;
      }
      setDetail({ app: application, envelope, plaintext });
    } catch (err) {
      setDetail({
        app: application,
        error: err instanceof Error ? err.message : 'Failed to load IPFS envelope',
      });
    } finally {
      setDetailLoading(false);
    }
  };

  const handleStatusChange = async (id: string, _type: Application['type'], newStatus: string) => {
    try {
      const raw = web3.getRawProvider();
      if (!raw || !web3.chainId) throw new Error('Connect an admin wallet');
      if (newStatus !== 'approved' && newStatus !== 'rejected') return;
      await reviewOnRegistry({
        rawProvider: raw,
        chainId: web3.chainId,
        id: Number(id),
        status: newStatus,
      });

      setApplications(apps =>
        apps.map(app =>
          app.id === id ? { ...app, status: newStatus } : app
        )
      );
    } catch (err) {
      console.error('Failed to update application status:', err);
    }
  };

  const ApplicationRow = ({ index, style }: { index: number; style: React.CSSProperties }) => {
    const application = filteredApplications[index];
    if (!application) return null;

    return (
      <div style={style} className="border-b border-gray-200 hover:bg-gray-50">
        <div className="px-6 py-4 flex items-center">
          <div className="flex-1">
            <div className="text-sm font-medium text-gray-900">
              DID #{application.did_id ?? '—'}
            </div>
            <div className="text-sm font-mono text-gray-500">
              {application.wallet_address
                ? `${application.wallet_address.slice(0, 6)}...${application.wallet_address.slice(-4)}`
                : 'encrypted'}
            </div>
          </div>
          <div className="flex-1">
            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-primary-100 text-primary">
              {application.type}
            </span>
          </div>
          <div className="flex-1">
            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
              application.status === 'approved'
                ? 'bg-green-100 text-green-800'
                : application.status === 'rejected'
                ? 'bg-red-100 text-red-800'
                : 'bg-yellow-100 text-yellow-800'
            }`}>
              {application.status}
            </span>
          </div>
          <div className="flex-1 text-sm text-gray-500">
            {new Date(application.created_at).toLocaleDateString()}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => handleStatusChange(application.id, application.type, 'approved')}
              className="text-green-600 hover:text-green-900"
              title="Approve"
            >
              <Check className="h-5 w-5" />
            </button>
            <button
              onClick={() => handleStatusChange(application.id, application.type, 'rejected')}
              className="text-red-600 hover:text-red-900"
              title="Reject"
            >
              <X className="h-5 w-5" />
            </button>
            <button
              onClick={() => void handleView(application)}
              className="text-primary hover:text-primary-600"
              title="View Details"
            >
              <Eye className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (!allowed) {
    return (
      <Section className="pt-28">
        <div className="flex justify-center items-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
        </div>
      </Section>
    );
  }

  return (
    <Section className="pt-28">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-6xl mx-auto"
      >
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-4">Review Applications</h1>
          <p className="text-gray-600">
            Review and manage all applications submitted to the platform.
          </p>
          {isLoading && (
            <p className="mt-2 text-sm text-gray-500">Loading applications…</p>
          )}
          {error && (
            <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
              {error}
              <Button
                variant="outline"
                className="mt-3"
                onClick={() => void loadApplications(0)}
              >
                Try Again
              </Button>
            </div>
          )}
          <label className="mt-4 block text-sm text-gray-600">
            Reviewer unwrap key (local only — never stored)
            <input
              type="password"
              value={reviewerSecret}
              onChange={(e) => setReviewerSecret(e.target.value)}
              placeholder="X25519 secret hex"
              className="mt-1 w-full max-w-xl rounded-lg border px-3 py-2 font-mono text-sm"
            />
          </label>
        </div>

        {detail && (
          <div className="mb-6 rounded-lg border border-gray-200 bg-white p-6">
            <div className="mb-3 flex items-start justify-between gap-4">
              <div>
                <h2 className="text-lg font-semibold">
                  Application #{detail.app.id} · DID #{detail.app.did_id ?? '—'}
                </h2>
                <p className="text-sm text-gray-500 font-mono break-all">{detail.app.uri || 'no uri'}</p>
              </div>
              <Button variant="outline" size="sm" onClick={() => setDetail(null)}>
                Close
              </Button>
            </div>
            {detailLoading && <p className="text-sm text-gray-500">Loading IPFS envelope…</p>}
            {detail.error && <p className="text-sm text-red-600">{detail.error}</p>}
            {detail.envelope && (
              <dl className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <dt className="text-gray-500">Country</dt>
                  <dd className="font-medium">{detail.envelope.public.country}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Role</dt>
                  <dd className="font-medium">{detail.envelope.public.role}</dd>
                </div>
                {detail.envelope.public.officerDid != null && (
                  <div>
                    <dt className="text-gray-500">Officer DID</dt>
                    <dd className="font-medium">#{detail.envelope.public.officerDid}</dd>
                  </div>
                )}
              </dl>
            )}
            {detail.plaintext && (
              <pre className="mt-4 max-h-64 overflow-auto rounded bg-gray-50 p-3 text-xs">
                {JSON.stringify(detail.plaintext, null, 2)}
              </pre>
            )}
            {detail.envelope && !detail.plaintext && !detail.error && (
              <p className="mt-3 text-sm text-gray-500">
                Paste the reviewer unwrap key above and reopen to decrypt PII locally.
              </p>
            )}
          </div>
        )}

        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-grow">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search by wallet or DID..."
                    onChange={(e) => debouncedSearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                  <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                </div>
              </div>
              <div className="flex gap-2">
                <select
                  value={filter}
                  onChange={(e) => setFilter(e.target.value as any)}
                  className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="all">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
            </div>
          </div>

          <div style={{ height: '600px' }}>
            <AutoSizer>
              {({ height, width }) => (
                <List
                  height={height}
                  itemCount={filteredApplications.length}
                  itemSize={80}
                  width={width}
                  onItemsRendered={({ visibleStopIndex }) => {
                    if (
                      hasMore &&
                      visibleStopIndex > 0 &&
                      visibleStopIndex === filteredApplications.length - 1
                    ) {
                      setPage(p => p + 1);
                      loadApplications(page + 1);
                    }
                  }}
                >
                  {ApplicationRow}
                </List>
              )}
            </AutoSizer>
          </div>

          {filteredApplications.length === 0 && (
            <div className="p-6 text-center text-gray-500">
              No applications found matching your criteria.
            </div>
          )}
        </div>
      </motion.div>
    </Section>
  );
};

export default AdminApplications;
